// #98 MeetingEvent (`event`, DC-141): the meeting event card — G7's Itinerary grid (When · Format · With · Where / link)
// grown by the two stored fields *Meeting ID* (mono, --text-3) and *Delivery status* (the delivery pill), with the
// Teams link drawn as a link and its own copy IconButton, and the hidden plain itinerary *Copy itinerary* copies. G5's
// Sync strip sits under the card's header: synchronizing, delivered, or the failure strip (F4a's ErrorStrip, the Er
// state). The Coordinator's page actions *Reschedule* and *Cancel* are ghost buttons on the right, drawn only for a seat
// that holds them (DC-24); an attendee sees the same card without them.

import type { ReactNode } from "react";
import { Button, Card, copyText, CORE_LABELS, IconButton, Pill, TextLink, type Blocked } from "../core";
import { ACTION_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { Sync, SYNC_LABELS, type SyncProps } from "../g5";
import { Itinerary, ITINERARY_LABELS, SCHEDULING_LABELS, type ItineraryItem } from "../g7";
import { EVENT_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface MeetingEventTeams {
  /** The persisted Teams URL ("Teams link is persisted."): the link's target and what the copy button copies. */
  url: string;
  onCopy?: ((url: string) => void) | undefined;
  hs?: Hs | undefined;
  copyHs?: Hs | undefined;
}

export interface MeetingEventProps {
  /** The title line: the meeting template and the requisition (default *Outlook event*). */
  title?: ReactNode | undefined;
  /** When · Format · With · Where / link (the itinerary rows; the Teams link, when given, is added to Where / link). */
  items: readonly ItineraryItem[];
  teams?: MeetingEventTeams | undefined;
  /** The stored meeting ID (mono). */
  meetingId?: string | undefined;
  /** The integration strip under the header (G5 Sync): its status, the failure's details and Retry. */
  sync?: SyncProps | undefined;
  /** The line under the grid. */
  note?: ReactNode | undefined;
  /** The plain text *Copy itinerary* copies (default from the rows). */
  itineraryText?: string | undefined;
  onCopyItinerary?: ((text: string) => void) | undefined;
  /** The itinerary's copy control in words (G7 Itinerary `copyInWords`; DC-241, a scheduling card has no icon alone). */
  copyInWords?: boolean | undefined;
  /** The page actions (Coordinator): absent unless the seat holds them. */
  onReschedule?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  canReschedule?: boolean | undefined;
  canCancel?: boolean | undefined;
  rescheduleBlocked?: Blocked | undefined;
  cancelBlocked?: Blocked | undefined;
  /** The card is loading: a skeleton at the card's final height. */
  loading?: boolean | undefined;
  can?: boolean | undefined;
  /** Responses (#99) or another section under the grid. */
  children?: ReactNode;
  hs?: { copyItinerary?: Hs | undefined; reschedule?: Hs | undefined; cancel?: Hs | undefined } | undefined;
  className?: string | undefined;
}

const DELIVERY_WORD: Readonly<Record<NonNullable<SyncProps["status"]>, string>> = {
  synchronizing: SYNC_LABELS.synchronizing,
  delivered: SYNC_LABELS.delivered,
  failure: CORE_LABELS.syncFailure,
};

export function MeetingEvent({
  title = EVENT_LABELS.outlookEvent, items, teams, meetingId, sync, note, itineraryText: text, onCopyItinerary, copyInWords, onReschedule, onCancel,
  canReschedule, canCancel, rescheduleBlocked, cancelBlocked, loading, can, children, hs = {}, className,
}: MeetingEventProps) {
  if (can === false) return null;
  const copyTeams = () => {
    if (!teams) return;
    void copyText(teams.url).then((done) => { if (done) teams.onCopy?.(teams.url); });
  };
  const rows: ItineraryItem[] = items.map(([term, value]) => {
    if (term !== ITINERARY_LABELS.whereLink || !teams) return [term, value] as const;
    return [term, (
      <span className="mc-ev-where">
        {value !== undefined && value !== null && value !== "" ? <span>{value}</span> : null}
        <TextLink href={teams.url} icon="video" hs={teams.hs}>{SCHEDULING_LABELS.teamsLink}</TextLink>
        <IconButton sm icon="copy" label={EVENT_LABELS.teamsUrl} onClick={copyTeams} hs={teams.copyHs} />
      </span>
    )] as const;
  });
  if (meetingId !== undefined) rows.push([EVENT_LABELS.meetingId, <span className="mc-mono mc-ev-id">{meetingId}</span>]);
  if (sync) {
    rows.push([SYNC_LABELS.deliveryStatus, (
      <Pill tone={sync.status === "delivered" ? "ok" : sync.status === "failure" ? "red" : "info"}>
        {DELIVERY_WORD[sync.status]}
      </Pill>
    )]);
  }
  const actions = (
    <>
      <Button sm tone="ghost" icon="refresh" can={canReschedule === true} blocked={rescheduleBlocked} onClick={onReschedule} hs={hs.reschedule}>
        {ACTION_LABELS.reschedule}
      </Button>
      <Button sm tone="ghost" icon="x" can={canCancel === true} blocked={cancelBlocked} onClick={onCancel} hs={hs.cancel}>
        {ACTION_LABELS.cancel}
      </Button>
    </>
  );
  const hasActions = canReschedule === true || canCancel === true;
  return (
    <Card
      title={title}
      icon="calendar"
      actions={hasActions ? actions : undefined}
      loading={loading ? { height: 228 } : undefined}
      className={cx("mc-ev", className)}
    >
      {sync ? <Sync {...sync} className={cx("mc-ev-sync", sync.className)} /> : null}
      <Itinerary items={rows} note={note} text={text} onCopy={onCopyItinerary} copyInWords={copyInWords} hs={hs.copyItinerary} className="mc-ev-itin" />
      {children}
    </Card>
  );
}
